import { Button, Card, useToast } from '@helix-ds/react'
import React, { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { usePreferencesContext } from '../features/preferences/usePreferencesContext'
import IntlProviderWithLocale from '../i18n/IntlProviderWithLocale'
import { getOrderedLocales } from '../i18n/orderedLocales'
import { DEFAULT_LOCALE, LOCALE_CATALOG, type SupportedLocale } from '../i18n/types'

const TranslateIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" className="h-14 w-14" aria-hidden="true">
    <path
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="m13 19 3.5-9 3.5 9m-6.125-2h5.25M3 7h7m0 0h2m-2 0c0 1.63-.793 3.926-2.239 5.655M7.5 6.818V5m.261 7.655C6.79 13.82 5.521 14.725 4 15m3.761-2.345L5 10m2.761 2.655L10.2 15"
    />
  </svg>
)

function localeBadge(locale: SupportedLocale): string {
  const parts = locale.split('-')
  return (parts[1] ?? parts[0]).toUpperCase()
}

type LanguageSelectContentProps = {
  selected: SupportedLocale | null
  isSubmitting: boolean
  error: boolean
  onSelect: (locale: SupportedLocale) => void
  setIsSubmitting: (value: boolean) => void
  setError: (value: boolean) => void
}

const LanguageSelectContent: React.FC<LanguageSelectContentProps> = ({
  selected,
  isSubmitting,
  error,
  onSelect,
  setIsSubmitting,
  setError,
}) => {
  const intl = useIntl()
  const { updateLanguage } = usePreferencesContext()
  const navigate = useNavigate()
  const { addNotification } = useToast()
  const heading = intl.formatMessage({ id: 'languageSelect.heading' })

  const handleContinue = async () => {
    if (!selected) return
    setIsSubmitting(true)
    setError(false)
    try {
      await updateLanguage(selected)
      addNotification({
        type: 'success',
        message: intl.formatMessage({ id: 'languageSelect.toast.saved' }),
      })
      navigate('/home')
    } catch {
      setIsSubmitting(false)
      setError(true)
    }
  }

  return (
    <div className="bg-bg-secondary min-h-screen pb-52 sm:pb-32">
      <div className="flex justify-center px-4 pt-12 pb-8">
        <img src="/icons/Inflection_Logo_Dark_LG.png" alt="Inflection Medicine" className="h-10 w-auto sm:h-[42px]" />
      </div>

      <div className="mx-auto w-full max-w-[1024px] px-4 sm:px-8">
        <Card padding="lg">
          <div className="flex flex-col gap-8">
            <div className="bg-success-50 text-success-500 flex h-24 w-24 items-center justify-center rounded-full">
              <TranslateIcon />
            </div>

            <h1 className="text-text-heading text-2xl leading-tight font-semibold tracking-tight sm:text-4xl sm:leading-9">
              <FormattedMessage id="languageSelect.heading" />
            </h1>

            <div className="flex flex-col" role="listbox" aria-label={heading}>
              {getOrderedLocales().map((locale) => {
                const isSelected = selected === locale
                return (
                  <button
                    key={locale}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => onSelect(locale)}
                    disabled={isSubmitting}
                    className={`flex items-center gap-1.5 rounded-[6px] px-2 py-6 text-base leading-6 transition-colors disabled:cursor-not-allowed ${
                      isSelected
                        ? 'bg-background-brand-softer border-border-brand text-text-brand border'
                        : 'border-border-base text-text-heading border-b'
                    }`}
                  >
                    <span className="hidden sm:inline" aria-hidden="true">
                      {localeBadge(locale)} -{' '}
                    </span>
                    <span>{LOCALE_CATALOG[locale].endonym}</span>
                  </button>
                )
              })}
            </div>

            {error && (
              <p role="alert" className="text-error-600 text-sm">
                <FormattedMessage id="languageSelect.error.save" />
              </p>
            )}
          </div>
        </Card>
      </div>

      <div className="bg-background-primary border-border-base fixed inset-x-0 bottom-0 border-t">
        <div className="mx-auto flex max-w-[1024px] flex-col gap-4 p-8 sm:flex-row sm:items-center">
          <div className="bg-background-primary-soft border-border-base flex-1 rounded-lg border p-4">
            <p className="text-success-600 text-sm leading-5">
              <FormattedMessage id="languageSelect.footer.hint" />
            </p>
          </div>

          <Button
            variant="primary"
            size="lg"
            disabled={!selected || isSubmitting}
            onClick={handleContinue}
            className="w-full sm:w-auto"
          >
            <FormattedMessage id="languageSelect.footer.continue" />
          </Button>
        </div>
      </div>
    </div>
  )
}

const LanguageSelect: React.FC = () => {
  const [selected, setSelected] = useState<SupportedLocale | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState(false)
  const activeLocale = selected ?? DEFAULT_LOCALE

  return (
    <IntlProviderWithLocale locale={activeLocale}>
      <LanguageSelectContent
        selected={selected}
        isSubmitting={isSubmitting}
        error={error}
        onSelect={setSelected}
        setIsSubmitting={setIsSubmitting}
        setError={setError}
      />
    </IntlProviderWithLocale>
  )
}

export default LanguageSelect
